import { useState } from "react";
import { Link } from "react-router-dom";
import { Label } from "./ui-bits";
import { BMI_BANDS, calcBmi, waistToHeight, ftInToCm, stLbToKg, round } from "@/data/tools";

const TONE = { teal: "text-teal", amber: "text-amber-300", rose: "text-rose" };

export default function BmiCalculator() {
  const [units, setUnits] = useState("metric");
  const [m, setM] = useState({ heightCm: "", weightKg: "", waistCm: "" });
  const [i, setI] = useState({ ft: "", inch: "", st: "", lb: "", waistIn: "" });

  const heightCm = units === "metric" ? Number(m.heightCm) : ftInToCm(i.ft, i.inch);
  const weightKg = units === "metric" ? Number(m.weightKg) : stLbToKg(i.st, i.lb);
  const waistCm = units === "metric" ? Number(m.waistCm) : Number(i.waistIn) * 2.54;

  const res = calcBmi({ heightCm, weightKg });
  const whtr = waistCm ? waistToHeight(waistCm, heightCm) : null;

  const setMetric = (k) => (e) => setM((s) => ({ ...s, [k]: e.target.value }));
  const setImp = (k) => (e) => setI((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="bmi-calculator">
      <div className="lg:col-span-5 card-line rounded-2xl p-6 sm:p-8 space-y-5">
        <div className="flex items-center justify-between">
          <Label>Your measurements</Label>
          <div className="inline-flex rounded-full border border-line p-0.5">
            {["metric", "imperial"].map((u) => (
              <button key={u} type="button" onClick={() => setUnits(u)} className={`px-3 py-1 rounded-full text-xs font-mono capitalize transition-colors ${units === u ? "bg-teal text-ink" : "text-mute"}`} data-testid={`bmi-units-${u}`}>
                {u}
              </button>
            ))}
          </div>
        </div>

        {units === "metric" ? (
          <>
            <Field label="Height (cm)" value={m.heightCm} onChange={setMetric("heightCm")} placeholder="e.g. 180" testId="bmi-height-cm" />
            <Field label="Weight (kg)" value={m.weightKg} onChange={setMetric("weightKg")} placeholder="e.g. 88" testId="bmi-weight-kg" />
            <Field label="Waist (cm) — optional" value={m.waistCm} onChange={setMetric("waistCm")} placeholder="Measured at the navel" testId="bmi-waist-cm" />
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Height (ft)" value={i.ft} onChange={setImp("ft")} placeholder="5" testId="bmi-height-ft" />
              <Field label="(in)" value={i.inch} onChange={setImp("inch")} placeholder="11" testId="bmi-height-in" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Weight (st)" value={i.st} onChange={setImp("st")} placeholder="13" testId="bmi-weight-st" />
              <Field label="(lb)" value={i.lb} onChange={setImp("lb")} placeholder="12" testId="bmi-weight-lb" />
            </div>
            <Field label="Waist (in) — optional" value={i.waistIn} onChange={setImp("waistIn")} placeholder="Measured at the navel" testId="bmi-waist-in" />
          </>
        )}
      </div>

      <div className="lg:col-span-7 card-line rounded-2xl p-6 sm:p-8">
        <Label>Result</Label>
        <div className="flex flex-wrap items-end gap-6 mt-5">
          <div>
            <p className="mono-label text-dusk">Body mass index</p>
            <p className="font-display text-6xl text-teal leading-none mt-2" data-testid="bmi-value">{res ? res.bmi : "—"}</p>
          </div>
          <div className="pb-1">
            <p className="mono-label text-dusk">Category (NHS)</p>
            <p className={`font-display text-2xl tracking-wide mt-1 ${res ? TONE[res.band.tone] : "text-dusk"}`} data-testid="bmi-category">{res ? res.band.label : "Enter height and weight"}</p>
          </div>
          {whtr && (
            <div className="pb-1">
              <p className="mono-label text-dusk">Waist-to-height</p>
              <p className={`font-display text-2xl tracking-wide mt-1 ${whtr < 0.5 ? "text-teal" : whtr < 0.6 ? "text-amber-300" : "text-rose"}`} data-testid="bmi-whtr">{whtr}</p>
            </div>
          )}
        </div>

        <div className="mt-7 h-2 rounded-full overflow-hidden flex" aria-hidden="true">
          {[
            ["bg-amber-300/60", 18.5 - 15],
            ["bg-teal/70", 25 - 18.5],
            ["bg-amber-300/60", 30 - 25],
            ["bg-rose/60", 40 - 30],
          ].map(([cls, w], idx) => (
            <div key={idx} className={cls} style={{ width: `${(w / 25) * 100}%` }} />
          ))}
        </div>
        {res && (
          <div className="relative h-4">
            <span className="absolute -translate-x-1/2 text-teal text-xs font-mono" style={{ left: `${Math.min(100, Math.max(0, ((res.bmi - 15) / 25) * 100))}%` }}>▲ {res.bmi}</span>
          </div>
        )}

        <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-xs text-dusk">
          {BMI_BANDS.map((b, idx) => (
            <li key={b.label} className="flex justify-between gap-3 border-b border-line py-1.5">
              <span className="text-bone/75">{b.label}</span>
              <span className="font-mono">{idx === 0 ? `< ${b.max}` : b.max === Infinity ? `≥ ${BMI_BANDS[idx - 1].max}` : `${BMI_BANDS[idx - 1].max}–${round(b.max - 0.1, 1)}`}</span>
            </li>
          ))}
        </ul>

        <div className="mt-6 text-sm text-mute leading-relaxed space-y-3">
          <p>
            <span className="text-bone/85">Why this matters for testosterone:</span> excess body fat converts
            testosterone to oestrogen, lowers SHBG and dampens the pituitary signal. Losing around 10% of body
            weight is one of the best-evidenced ways to raise testosterone — for some men it is the treatment.
          </p>
          <p className="text-xs text-dusk">
            BMI is a population screening tool and can misclassify very muscular men; waist-to-height ratio
            (aim below 0.5) adds useful context. Neither is a diagnosis. Read more in{" "}
            <Link to="/learn/lifestyle-and-testosterone" className="text-teal hover:underline" data-testid="bmi-lifestyle-link">Lifestyle and testosterone</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="mono-label text-dusk block mb-2">{label}</span>
      <input type="number" inputMode="decimal" min="0" className="field" value={value} onChange={onChange} placeholder={placeholder} data-testid={testId} />
    </label>
  );
}
